import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, Search, FolderPlus, Folder, X, FileUp, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function Files() {
  const [files, setFiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all"); // "all" | "uncategorized" | <category_id>
  const [showUpload, setShowUpload] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  // ---- Multi-file drag & drop state ----
  // queue: [{ id, file, status: "queued" | "uploading" | "done" | "error", error? }]
  const [queue, setQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);  // handles dragenter/dragleave bubbling reliably

  const loadFiles = async (catId = activeCat, qStr = q) => {
    const params = {};
    if (qStr) params.q = qStr;
    if (catId && catId !== "all") params.category_id = catId;
    const { data } = await api.get("/files", { params });
    setFiles(data);
  };

  const loadCats = async () => {
    const { data } = await api.get("/file-categories");
    setCategories(data);
  };

  useEffect(() => { loadCats(); loadFiles("all", ""); }, []);
  useEffect(() => { loadFiles(activeCat, q); /* eslint-disable-next-line */ }, [activeCat]);

  const onPickCategory = (catId) => setActiveCat(catId);

  const onSearch = () => loadFiles(activeCat, q);

  const onCreateCat = async (e) => {
    e?.preventDefault();
    if (!newCatName.trim()) return;
    try {
      await api.post("/file-categories", { name: newCatName.trim() });
      toast.success("Category created");
      setNewCatName("");
      setShowNewCat(false);
      await loadCats();
    } catch { toast.error("Could not create category"); }
  };

  const onDeleteCat = async (cat) => {
    if (!confirm(`Delete category "${cat.name}"? Files in it become uncategorized.`)) return;
    try {
      await api.delete(`/file-categories/${cat.id}`);
      toast.success("Category deleted");
      if (activeCat === cat.id) setActiveCat("all");
      await loadCats();
      await loadFiles();
    } catch { toast.error("Could not delete category"); }
  };

  const reassignFile = async (fileId, catId) => {
    try {
      await api.patch(`/files/${fileId}`, { category_id: catId || null });
      toast.success("Category updated");
      await loadFiles();
      await loadCats();
    } catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/files/${id}`);
    loadFiles(); loadCats();
  };

  const download = async (f) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(`/files/${f.id}/download`, { params: token ? { auth: token } : {}, responseType: "blob" });
    const blob = new Blob([res.data], { type: f.content_type });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = f.original_filename; a.click();
    URL.revokeObjectURL(u);
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  // ----------------- Multi-file upload (drag-and-drop) -----------------
  /** Upload one file. Picks the active category if the user is filtering on one. */
  const uploadOne = useCallback(async (item) => {
    const targetCatId =
      activeCat !== "all" && activeCat !== "uncategorized" ? activeCat : "";
    const fd = new FormData();
    fd.append("file", item.file);
    if (targetCatId) fd.append("category_id", targetCatId);
    try {
      await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQueue((cur) => cur.map((x) => (x.id === item.id ? { ...x, status: "done" } : x)));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      setQueue((cur) => cur.map((x) => (x.id === item.id ? { ...x, status: "error", error: msg } : x)));
    }
  }, [activeCat]);

  /** Accept either a FileList (from <input>) or an array of File. Queues + uploads in parallel. */
  const enqueueAndUpload = useCallback(async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (arr.length === 0) return;
    const items = arr.map((f) => ({
      id: `qf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      status: "uploading",
    }));
    setQueue((cur) => [...items, ...cur]);
    // Fire in parallel; each settles its own row
    await Promise.allSettled(items.map((it) => uploadOne(it)));
    // Refresh the table once everything has settled
    await loadFiles();
    await loadCats();
    const okCount = items.length;
    toast.success(`Uploaded ${okCount} file${okCount > 1 ? "s" : ""}`);
  }, [uploadOne]);

  const clearCompleted = () => setQueue((cur) => cur.filter((x) => x.status !== "done"));

  // Window-level drag handlers — show the dropzone overlay whenever the user drags any
  // file over the page (not just over the dropzone box). dragCounter avoids flicker
  // caused by dragenter/dragleave firing for every child element.
  useEffect(() => {
    const onEnter = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const onOver = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();  // required to allow drop
    };
    const onLeave = (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };
    const onDrop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      enqueueAndUpload(e.dataTransfer.files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [enqueueAndUpload]);

  const totalCount = categories.reduce((s, c) => s + (c.file_count || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up">
      {isDragging && (
        <div
          className="fixed inset-0 z-[60] bg-yellow-400/80 backdrop-blur-sm border-8 border-dashed border-zinc-900 flex flex-col items-center justify-center pointer-events-none"
          data-testid="files-drag-overlay"
        >
          <FileUp className="w-20 h-20 mb-4 text-zinc-900" />
          <div className="font-heading text-3xl font-black text-zinc-900">DROP FILES TO UPLOAD</div>
          <div className="font-mono text-sm text-zinc-800 mt-1">All files upload in parallel — categorise them after</div>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">DOCUMENT VAULT</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Files</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Organized by category · org-wide search · attached to jobs</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNewCat(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-category-btn">
            <FolderPlus className="w-4 h-4" /> NEW CATEGORY
          </button>
          <button onClick={() => setShowUpload(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="file-upload-btn">
            <Upload className="w-4 h-4" /> UPLOAD FILE
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Category sidebar */}
        <aside className="brut-card p-3 h-fit" data-testid="categories-sidebar">
          <div className="label-mono mb-2">CATEGORIES</div>
          <ul className="space-y-1">
            <li>
              <button
                onClick={() => onPickCategory("all")}
                data-testid="cat-all"
                className={`w-full text-left flex items-center justify-between px-2 py-1.5 text-sm font-mono border-l-4 transition-colors ${activeCat === "all" ? "border-zinc-900 bg-yellow-100 font-bold" : "border-transparent hover:bg-zinc-50"}`}
              >
                <span className="flex items-center gap-2"><Folder className="w-3.5 h-3.5" /> ALL FILES</span>
                <span className="text-xs text-zinc-500">{totalCount}</span>
              </button>
            </li>
            <li>
              <button
                onClick={() => onPickCategory("uncategorized")}
                data-testid="cat-uncategorized"
                className={`w-full text-left flex items-center justify-between px-2 py-1.5 text-sm font-mono border-l-4 transition-colors ${activeCat === "uncategorized" ? "border-zinc-900 bg-yellow-100 font-bold" : "border-transparent hover:bg-zinc-50"}`}
              >
                <span className="flex items-center gap-2"><Folder className="w-3.5 h-3.5 opacity-40" /> UNCATEGORIZED</span>
              </button>
            </li>
            {categories.map((c) => (
              <li key={c.id} className="group flex items-center">
                <button
                  onClick={() => onPickCategory(c.id)}
                  data-testid={`cat-${c.slug}`}
                  className={`flex-1 text-left flex items-center justify-between px-2 py-1.5 text-sm font-mono border-l-4 transition-colors ${activeCat === c.id ? "border-zinc-900 bg-yellow-100 font-bold" : "border-transparent hover:bg-zinc-50"}`}
                >
                  <span className="flex items-center gap-2 truncate"><Folder className="w-3.5 h-3.5" /> {c.name}</span>
                  <span className="text-xs text-zinc-500 ml-2">{c.file_count || 0}</span>
                </button>
                {!c.is_default && (
                  <button
                    onClick={() => onDeleteCat(c)}
                    className="opacity-0 group-hover:opacity-100 text-red-600 p-1"
                    title="Delete category"
                    data-testid={`delete-cat-${c.slug}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </aside>

        {/* Files area */}
        <div className="space-y-3">
          <FilesDropzone
            isDragging={isDragging}
            onPick={(files) => enqueueAndUpload(files)}
            activeCatLabel={
              activeCat === "all"
                ? null
                : activeCat === "uncategorized"
                ? "uncategorized"
                : categories.find((c) => c.id === activeCat)?.name
            }
          />
          {queue.length > 0 && (
            <UploadQueue queue={queue} onClear={clearCompleted} fmtSize={fmtSize} />
          )}

          <div className="brut-card p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                placeholder="Search files by name…"
                className="w-full brut-input pl-9 pr-3 py-2 text-sm font-mono"
                data-testid="file-search-input"
              />
            </div>
          </div>

          <div className="brut-card overflow-x-auto" data-testid="files-table">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <Th>Filename</Th><Th>Category</Th><Th>Type</Th><Th>Size</Th><Th>Uploaded By</Th><Th>Date</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {files.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-zinc-400 font-mono text-xs">NO FILES — UPLOAD TO BEGIN</td></tr>
                )}
                {files.map((f) => (
                  <tr key={f.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                    <Td className="font-bold">{f.original_filename}</Td>
                    <Td>
                      <select
                        value={f.category_id || ""}
                        onChange={(e) => reassignFile(f.id, e.target.value)}
                        className="brut-input px-1 py-0.5 text-xs font-mono max-w-[140px]"
                        data-testid={`file-cat-select-${f.id}`}
                      >
                        <option value="">— uncategorized —</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </Td>
                    <Td><span className="font-mono text-xs">{f.content_type?.split("/")[1] || "—"}</span></Td>
                    <Td><span className="font-mono">{fmtSize(f.size)}</span></Td>
                    <Td><span className="text-xs">{f.user_name}</span></Td>
                    <Td><span className="font-mono text-xs">{f.created_at?.slice(0, 10)}</span></Td>
                    <Td>
                      <div className="flex gap-1">
                        <button onClick={() => download(f)} className="brut-btn-secondary p-1.5" data-testid={`download-file-${f.id}`}><Download className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(f.id)} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showUpload && (
        <UploadModal
          categories={categories}
          defaultCategoryId={activeCat !== "all" && activeCat !== "uncategorized" ? activeCat : ""}
          onClose={() => setShowUpload(false)}
          onUploaded={async () => { await loadFiles(); await loadCats(); }}
        />
      )}

      {showNewCat && (
        <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowNewCat(false)}>
          <form onSubmit={onCreateCat} className="brut-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-2xl font-black mb-2">New Category</h2>
            <p className="text-xs font-mono text-zinc-600 mb-4">Add a category to organize files going forward.</p>
            <input
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="e.g. Site Surveys"
              className="w-full brut-input px-3 py-2 text-sm"
              data-testid="new-cat-name-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setShowNewCat(false)} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
              <button type="submit" className="brut-btn px-4 py-2 text-sm" data-testid="new-cat-submit">CREATE</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function UploadModal({ categories, defaultCategoryId, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [categoryId, setCategoryId] = useState(defaultCategoryId || "");
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (categoryId) fd.append("category_id", categoryId);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      onUploaded?.();
      onClose();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} className="brut-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl font-black mb-2">Upload File</h2>
        <p className="text-xs font-mono text-zinc-600 mb-4">Pick a category so this file shows up in the right folder.</p>
        <label className="block mb-3">
          <div className="label-mono mb-1">CATEGORY</div>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="brut-input w-full px-3 py-2 text-sm font-mono"
            data-testid="upload-category-select"
          >
            <option value="">— uncategorized —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block mb-3">
          <div className="label-mono mb-1">FILE</div>
          <input
            ref={fileRef}
            type="file"
            required
            data-testid="upload-file-input"
            className="w-full text-sm font-mono"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button type="submit" disabled={uploading} className="brut-btn px-4 py-2 text-sm disabled:opacity-60" data-testid="upload-submit-btn">
            {uploading ? "UPLOADING…" : "UPLOAD"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;

// ----------------------------- Multi-file dropzone -----------------------------
function FilesDropzone({ isDragging, onPick, activeCatLabel }) {
  const inputRef = useRef(null);
  const openPicker = () => inputRef.current?.click();
  return (
    <div
      className={`brut-card p-6 text-center cursor-pointer transition-colors ${
        isDragging ? "bg-yellow-100 border-zinc-900" : "bg-zinc-50 hover:bg-yellow-50"
      }`}
      onClick={openPicker}
      data-testid="files-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";  // allow re-picking the same file later
        }}
        data-testid="files-dropzone-input"
      />
      <FileUp className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
      <div className="font-heading text-lg font-bold">Drop files here or click to browse</div>
      <div className="font-mono text-xs text-zinc-600 mt-1">
        Multiple files supported.
        {activeCatLabel
          ? <> New uploads land in <b>{activeCatLabel.toUpperCase()}</b>.</>
          : <> Uploads land in <b>UNCATEGORIZED</b> — pick a category in the sidebar first to auto-tag.</>}
      </div>
    </div>
  );
}

// ----------------------------- Upload queue -----------------------------
function UploadQueue({ queue, onClear, fmtSize }) {
  const inProg = queue.filter((q) => q.status === "uploading").length;
  const done = queue.filter((q) => q.status === "done").length;
  const err = queue.filter((q) => q.status === "error").length;
  return (
    <div className="brut-card p-3" data-testid="upload-queue">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">
          UPLOAD QUEUE · {queue.length} file{queue.length === 1 ? "" : "s"}
          {inProg > 0 && ` · ${inProg} uploading`}
          {done > 0 && ` · ${done} done`}
          {err > 0 && ` · ${err} failed`}
        </div>
        {done + err > 0 && (
          <button onClick={onClear} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono" data-testid="queue-clear-btn">
            CLEAR COMPLETED
          </button>
        )}
      </div>
      <ul className="space-y-1 max-h-44 overflow-y-auto">
        {queue.map((q) => (
          <li
            key={q.id}
            className="flex items-center justify-between border border-zinc-200 px-2 py-1 font-mono text-xs bg-white"
            data-testid={`queue-row-${q.id}`}
          >
            <span className="truncate flex-1 mr-2">{q.file.name}</span>
            <span className="text-zinc-500 mr-3">{fmtSize(q.file.size)}</span>
            {q.status === "uploading" && <span className="flex items-center gap-1 text-blue-700"><Loader2 className="w-3 h-3 animate-spin" /> UPLOADING</span>}
            {q.status === "done" && <span className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3 h-3" /> DONE</span>}
            {q.status === "error" && (
              <span className="flex items-center gap-1 text-red-700" title={q.error || "Failed"}>
                <AlertTriangle className="w-3 h-3" /> FAILED
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
